import { AVAILABLE_MAP_ZONES, MARKET_TO_TSO_ZONE_MAPPING } from './constants';
import type { MarketZone } from '@app/common/static-data/energy-markets';
import type { ZoneData, CombinedGeoJSON } from './types';

/**
 * Calculate bounding box and center of a GeoJSON feature collection
 * Handles regions that cross the 180° meridian (like Alaska)
 */
const calculateZoneBoundsAndCenter = (
  geoJSON: any,
): { center: [number, number]; bounds: [[number, number], [number, number]] } => {
  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  const longitudes: number[] = [];

  // Helper function to process coordinates recursively
  const processCoordinates = (coords: any[]) => {
    if (typeof coords[0] === 'number') {
      // This is a coordinate pair [lng, lat]
      const [lng, lat] = coords;
      longitudes.push(lng);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    } else {
      // This is an array of coordinates, process each one
      coords.forEach(processCoordinates);
    }
  };

  // Process all features in the collection
  geoJSON.features.forEach((feature: any) => {
    if (feature.geometry && feature.geometry.coordinates) {
      processCoordinates(feature.geometry.coordinates);
    }
  });

  // Check if this region crosses the 180° meridian
  const crossesDateLine = maxLng - minLng > 180;

  let centerLng: number;
  if (crossesDateLine) {
    // For regions crossing the date line, calculate center differently
    // Convert negative longitudes to positive (add 360°) for calculation
    const adjustedLongitudes = longitudes.map((lng) => (lng < 0 ? lng + 360 : lng));
    const adjustedMinLng = Math.min(...adjustedLongitudes);
    const adjustedMaxLng = Math.max(...adjustedLongitudes);
    const adjustedCenterLng = (adjustedMinLng + adjustedMaxLng) / 2;

    // Convert back to standard longitude range (-180 to 180)
    centerLng = adjustedCenterLng > 180 ? adjustedCenterLng - 360 : adjustedCenterLng;
  } else {
    // Standard calculation for regions that don't cross the date line
    centerLng = (minLng + maxLng) / 2;
  }

  const centerLat = (minLat + maxLat) / 2;

  return {
    center: [centerLng, centerLat],
    bounds: [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
  };
};

export const loadTSOZones = async (
  availableZones?: MarketZone[],
): Promise<{
  combinedGeoJSON: CombinedGeoJSON;
  featureMap: Map<string, number[]>;
  zoneCenters: Map<string, [number, number]>;
  zoneBounds: Map<string, [[number, number], [number, number]]>;
}> => {
  try {
    // Use provided available zones or fall back to all zones
    const zonesToLoad = availableZones || AVAILABLE_MAP_ZONES;

    // Load zones that have TSO GeoJSON files available and are dynamically available
    const zonePromises = zonesToLoad.map(async (marketZone) => {
      try {
        const tsoZoneFileName = MARKET_TO_TSO_ZONE_MAPPING[marketZone.id];
        if (!tsoZoneFileName) {
          return null;
        }

        const response = await fetch(`/data/geojson/tso-zones/${tsoZoneFileName}.geojson`);
        if (!response.ok) {
          return null;
        }
        const data = await response.json();

        // Calculate bounds and center for this zone
        const { center, bounds } = calculateZoneBoundsAndCenter(data);

        return {
          marketZone: marketZone,
          tsoZoneFileName: tsoZoneFileName,
          data,
          center,
          bounds,
        } as ZoneData;
      } catch {
        return null;
      }
    });

    const zones = (await Promise.all(zonePromises)).filter(Boolean) as ZoneData[];

    // Create a combined GeoJSON FeatureCollection with unique IDs
    let featureId = 0;
    const featureMap = new Map<string, number[]>();
    const zoneCenters = new Map<string, [number, number]>();
    const zoneBounds = new Map<string, [[number, number], [number, number]]>();

    const combinedGeoJSON: CombinedGeoJSON = {
      type: 'FeatureCollection',
      features: zones.flatMap((zoneData) => {
        const zoneFeatures: number[] = [];
        const features = zoneData.data.features.map((feature: any) => {
          const uniqueId = featureId++;
          zoneFeatures.push(uniqueId);

          return {
            ...feature,
            id: uniqueId,
            properties: {
              ...feature.properties,
              market_zone_id: zoneData.marketZone.id,
              market_zone_label: zoneData.marketZone.label,
              tso_zone: zoneData.tsoZoneFileName, // Keep for backward compatibility
              zone_name: zoneData.marketZone.label, // Keep for backward compatibility
              feature_id: uniqueId,
            },
          };
        });

        // Map market zone ID to its feature IDs and center/bounds
        featureMap.set(zoneData.marketZone.id, zoneFeatures);
        if (zoneData.center) {
          zoneCenters.set(zoneData.marketZone.id, zoneData.center);
        }
        if ((zoneData as any).bounds) {
          zoneBounds.set(zoneData.marketZone.id, (zoneData as any).bounds);
        }

        return features;
      }),
    };

    return { combinedGeoJSON, featureMap, zoneCenters, zoneBounds };
  } catch (error) {
    console.error('Error loading TSO zones:', error);
    throw error;
  }
};
